import { quizSections } from '../data/questions';

// Testing utilities for development
export const testingUtils = {
  answerAllQuestions: async () => {
    console.log('🚀 Starting to answer all questions...');

    // Get current section from the visible questions
    const sectionElements = document.querySelectorAll('[id^="question-"]');
    let answeredCount = 0;

    for (let i = 0; i < sectionElements.length; i++) {
      const element = sectionElements[i];
      const questionId = element.id.replace('question-', '');

      // Find the question data
      let question = null;
      let sectionIndex = -1;

      for (let i = 0; i < quizSections.length; i++) {
        const foundQuestion = quizSections[i].questions.find(q => q.id === questionId);
        if (foundQuestion) {
          question = foundQuestion;
          sectionIndex = i;
          break;
        }
      }

      if (!question) {
        console.warn(`Question not found for ID: ${questionId}`);
        return;
      }

      if (question.type === 'multiple-choice') {
        // Find the correct option and trigger the change event
        const correctIndex = question.correct;
        if (correctIndex !== undefined) {
          // Look for the radio input with the correct value
          const radioInput = element.querySelector(`input[name="question-${questionId}"][value="${correctIndex}"]`) as HTMLInputElement;

          if (radioInput && !radioInput.checked) {
            // Get the label that wraps this radio input
            const label = radioInput.closest('label');
            if (label) {
              // Simulate a mouse click on the label - this should trigger React's onClick handler
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              label.dispatchEvent(clickEvent);

              answeredCount++;
              console.log(`✅ MCQ ${questionId}: Selected option ${correctIndex + 1}`);

              // Small delay to let React process the state change
              await new Promise(resolve => setTimeout(resolve, 100));
            } else {
              console.warn(`⚠️ Could not find label for question ${questionId}, option ${correctIndex}`);
            }
          } else if (!radioInput) {
            console.warn(`⚠️ Could not find radio input for question ${questionId}, option ${correctIndex}`);
          } else {
            console.log(`⚪ MCQ ${questionId}: Already answered`);
          }
        }
      } else if (question.type === 'free-response') {
        // Fill the textarea with a sample correct answer
        const textarea = element.querySelector('textarea') as HTMLTextAreaElement;

        if (textarea && textarea.value.trim() === '') {
          // Generate a sample answer based on key points
          let sampleAnswer = '';

          if (question.keyPoints && question.keyPoints.length > 0) {
            // Use the key points to create a comprehensive answer
            sampleAnswer = question.keyPoints.slice(0, question.minPoints || 3).join('. ') + '.';
          } else if (question.completeAnswer) {
            // Use the complete answer if available
            sampleAnswer = question.completeAnswer;
          } else {
            // Fallback generic answer
            sampleAnswer = 'Always follow proper safety protocols and procedures. Ensure all safety equipment is used correctly. Report any hazards immediately to the instructor.';
          }

          // Set the value and trigger change event
          textarea.value = sampleAnswer;

          // Trigger multiple events to ensure the component updates
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
          textarea.dispatchEvent(new Event('change', { bubbles: true }));

          answeredCount++;
          console.log(`✅ Free Response ${questionId}: Filled with sample answer`);

          // Small delay to let React process the state change
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Small delay between questions
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    console.log(`🎉 Answered ${answeredCount} questions on current section!`);
    return `Answered ${answeredCount} questions`;
  },

  answerCurrentSection: () => {
    return testingUtils.answerAllQuestions();
  },

  clearAllAnswers: () => {
    console.log('🧹 Clearing all answers...');

    const sectionElements = document.querySelectorAll('[id^="question-"]');
    let clearedCount = 0;

    sectionElements.forEach((element) => {
      // Clear multiple choice selections
      const radioInputs = element.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;
      radioInputs.forEach(radio => {
        if (radio.checked) {
          radio.checked = false;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          clearedCount++;
        }
      });

      // Clear free response text
      const textareas = element.querySelectorAll('textarea') as NodeListOf<HTMLTextAreaElement>;
      textareas.forEach(textarea => {
        if (textarea.value.trim() !== '') {
          textarea.value = '';
          textarea.dispatchEvent(new Event('change', { bubbles: true }));
          clearedCount++;
        }
      });
    });

    console.log(`🧹 Cleared ${clearedCount} answers!`);
    return `Cleared ${clearedCount} answers`;
  },

  clearProgress: () => {
    console.log('🗑️ Clearing all progress from localStorage...');
    try {
      localStorage.removeItem('quiz-progress');
      console.log('✅ Progress cleared! Refresh the page to start fresh.');
      return 'Progress cleared - refresh page';
    } catch (error) {
      console.error('❌ Failed to clear progress:', error);
      return 'Failed to clear progress';
    }
  },

  // Check sections status
  checkSectionsStatus: () => {
    console.log('📊 Checking all sections status...');
    console.log('Total sections available:', quizSections.length);

    quizSections.forEach((section, index) => {
      console.log(`\n📝 Section ${index + 1}: ${section.name}`);
      console.log(`   ID: ${section.id}`);
      console.log(`   Questions: ${section.questions.length}`);
      console.log(`   Order: ${section.order}`);

      // Check if section has DOM elements
      const sectionElements = document.querySelectorAll(`[id^="question-${section.questions[0]?.id?.substring(0, 3)}"]`);
      console.log(`   DOM elements found: ${sectionElements.length}`);
    });

    // Check progress data from localStorage
    try {
      const stored = localStorage.getItem('quiz-progress');
      if (stored) {
        const progress = JSON.parse(stored);
        console.log('\n💾 Progress sections:', progress.sections.length);
        progress.sections.forEach((section: any, index: number) => {
          console.log(`   ${index + 1}. ${section.name} (${section.id}) - Answers: ${section.answers?.length || 0}`);
        });
      }
    } catch (error) {
      console.log('No progress found in localStorage');
    }

    return 'Sections status logged to console';
  },

  // Test specific section
  testSection: (sectionIndex: number) => {
    if (sectionIndex < 0 || sectionIndex >= quizSections.length) {
      console.error('Invalid section index');
      return 'Invalid section index';
    }

    const section = quizSections[sectionIndex];
    console.log(`🧪 Testing Section ${sectionIndex + 1}: ${section.name}`);

    // Check if questions are present in DOM
    let foundQuestions = 0;
    section.questions.forEach(question => {
      const element = document.getElementById(`question-${question.id}`);
      if (element) foundQuestions++;
    });

    console.log(`   Expected questions: ${section.questions.length}`);
    console.log(`   Questions in DOM: ${foundQuestions}`);

    if (foundQuestions > 0) {
      console.log('✅ Section appears to be properly loaded');
      return `Section ${sectionIndex + 1} loaded correctly`;
    } else {
      console.log('❌ No questions found in DOM for this section');
      return `Section ${sectionIndex + 1} not loaded`;
    }
  },

  // Get question info for debugging
  getQuestionInfo: (questionId?: string) => {
    if (!questionId) {
      // Return all visible question IDs
      const elements = document.querySelectorAll('[id^="question-"]');
      const ids = Array.from(elements).map(el => el.id.replace('question-', ''));
      console.log('Visible question IDs:', ids);
      return ids;
    }

    // Find specific question
    for (const section of quizSections) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) {
        console.log('Question info:', question);
        return question;
      }
    }

    console.warn(`Question ${questionId} not found`);
    return null;
  }
};

// Expose to global window object for console access (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).answerAllQuestions = testingUtils.answerAllQuestions;
  (window as any).clearAllAnswers = testingUtils.clearAllAnswers;
  (window as any).clearProgress = testingUtils.clearProgress;
  (window as any).getQuestionInfo = testingUtils.getQuestionInfo;
  (window as any).checkSectionsStatus = testingUtils.checkSectionsStatus;
  (window as any).testSection = testingUtils.testSection;
  (window as any).testingUtils = testingUtils;

  console.log('🛠️ Testing utilities loaded! Available functions:');
  console.log('- answerAllQuestions() - Fill all visible questions with correct answers');
  console.log('- clearAllAnswers() - Clear all answers on current section');
  console.log('- clearProgress() - Clear all progress and start fresh');
  console.log('- getQuestionInfo(questionId?) - Get question data for debugging');
  console.log('- checkSectionsStatus() - Check status of all sections');
  console.log('- testSection(index) - Test specific section (0-based index)');
  console.log('- testingUtils - Access to all testing utilities');
}

export default testingUtils;